/**
 * Role type definitions for people at different levels.
 *
 * Trip-level: broad contacts for the whole trip (travel agent, tour operator, etc.)
 * Segment-level: staff assigned to a segment (cruise director, stateroom host, etc.)
 * Activity-level: staff encountered during a specific interaction (server, driver, etc.)
 */

export interface RoleTypeOption {
  value: string;
  label: string;
}

// Trip-level role types
export const TRIP_ROLE_TYPES: RoleTypeOption[] = [
  { value: "travel_agent", label: "Travel Agent" },
  { value: "tour_operator", label: "Tour Operator" },
  { value: "tour_guide", label: "Tour Guide" },
  { value: "concierge", label: "Concierge" },
  { value: "driver", label: "Driver" },
  { value: "planner", label: "Planner" },
  { value: "contact", label: "Contact" },
  { value: "other", label: "Other" },
];

// Segment-level role types (cruise crew, hotel staff, etc.)
export const SEGMENT_ROLE_TYPES: RoleTypeOption[] = [
  { value: "captain", label: "Captain" },
  { value: "cruise_director", label: "Cruise Director" },
  { value: "asst_cruise_director", label: "Asst. Cruise Director" },
  { value: "stateroom_host", label: "Stateroom Host" },
  { value: "head_waiter", label: "Head Waiter" },
  { value: "main_server", label: "Main Server" },
  { value: "asst_server", label: "Assistant Server" },
  { value: "guest_services", label: "Guest Services" },
  { value: "concierge", label: "Concierge" },
  { value: "other", label: "Other" },
];

// Activity/interaction-level role types
export const ACTIVITY_ROLE_TYPES: RoleTypeOption[] = [
  { value: "host", label: "Host" },
  { value: "server", label: "Server" },
  { value: "bartender", label: "Bartender" },
  { value: "tour_guide", label: "Tour Guide" },
  { value: "driver", label: "Driver" },
  { value: "performer", label: "Performer" },
  { value: "dj", label: "DJ" },
  { value: "entertainer", label: "Entertainer" },
  { value: "attendant", label: "Attendant" },
  { value: "photopass", label: "PhotoPass" },
  { value: "other", label: "Other" },
];

/**
 * Roles that fit a particular kind of activity, shown ahead of the general
 * list so the likely one is a tap away: a flight's pilot and flight attendants,
 * a hotel's front desk. Keyed by entity type.
 */
const ACTIVITY_ROLES_BY_TYPE: Partial<Record<string, RoleTypeOption[]>> = {
  TRANSPORTATION: [
    { value: "pilot", label: "Pilot" },
    { value: "first_officer", label: "First Officer" },
    { value: "flight_attendant", label: "Flight Attendant" },
    { value: "gate_agent", label: "Gate Agent" },
    { value: "driver", label: "Driver" },
    { value: "conductor", label: "Conductor" },
    { value: "captain", label: "Captain" },
  ],
  AIRPORT: [
    { value: "check_in_agent", label: "Check-in Agent" },
    { value: "gate_agent", label: "Gate Agent" },
    { value: "security_officer", label: "Security Officer" },
  ],
  RESTAURANT: [
    { value: "host", label: "Host" },
    { value: "server", label: "Server" },
    { value: "bartender", label: "Bartender" },
    { value: "sommelier", label: "Sommelier" },
    { value: "chef", label: "Chef" },
    { value: "manager", label: "Manager" },
  ],
  LOUNGE: [
    { value: "bartender", label: "Bartender" },
    { value: "server", label: "Server" },
    { value: "host", label: "Host" },
    { value: "entertainer", label: "Entertainer" },
  ],
  SHOW: [
    { value: "performer", label: "Performer" },
    { value: "host", label: "Host" },
    { value: "entertainer", label: "Entertainer" },
  ],
  EVENT: [
    { value: "host", label: "Host" },
    { value: "performer", label: "Performer" },
    { value: "dj", label: "DJ" },
    { value: "entertainer", label: "Entertainer" },
  ],
  EXCURSION: [
    { value: "tour_guide", label: "Tour Guide" },
    { value: "driver", label: "Driver" },
    { value: "captain", label: "Captain" },
    { value: "instructor", label: "Instructor" },
  ],
  ATTRACTION: [
    { value: "attendant", label: "Attendant" },
    { value: "performer", label: "Performer" },
    { value: "photopass", label: "PhotoPass" },
  ],
  HOTEL: [
    { value: "front_desk", label: "Front Desk" },
    { value: "concierge", label: "Concierge" },
    { value: "bellhop", label: "Bellhop" },
    { value: "housekeeping", label: "Housekeeping" },
  ],
  SHOP: [
    { value: "sales_associate", label: "Sales Associate" },
    { value: "cashier", label: "Cashier" },
  ],
};

/**
 * The roles to offer on an activity of the given type: the ones that fit it
 * first, then the rest of the general list, each once. "Other" is left out —
 * pickers offer a named "Other…" of their own, stored as the typed role.
 */
export function activityRoleTypesFor(type: string | null | undefined): RoleTypeOption[] {
  const seen = new Set<string>();
  const out: RoleTypeOption[] = [];
  for (const t of [...(type ? ACTIVITY_ROLES_BY_TYPE[type] ?? [] : []), ...ACTIVITY_ROLE_TYPES]) {
    if (t.value === "other" || seen.has(t.value)) continue;
    seen.add(t.value);
    out.push(t);
  }
  return out;
}

/**
 * The roles an activity of this type leads with — its own short list, the rest
 * kept behind "More…". Null when the type has no list of its own, and the
 * general list is short enough to show whole.
 */
export function featuredActivityRolesFor(type: string | null | undefined): string[] | null {
  const own = type ? ACTIVITY_ROLES_BY_TYPE[type] : undefined;
  return own ? own.map((t) => t.value) : null;
}

/**
 * A saved role list — a type's (`typeSettings/{TYPE}.staffRoles`) or a place's
 * (`customConfig.staff_roles`). Read defensively: anything malformed is
 * dropped, and a list that isn't there at all is null ("use the default").
 */
export function parseRoleList(raw: unknown): RoleTypeOption[] | null {
  if (!Array.isArray(raw)) return null;
  const out: RoleTypeOption[] = [];
  const seen = new Set<string>();
  for (const item of raw) {
    const r = item as Partial<RoleTypeOption> | null;
    const value = typeof r?.value === "string" ? r.value.trim() : "";
    if (!value || value === "other" || seen.has(value)) continue;
    const label = typeof r?.label === "string" && r.label.trim() ? r.label.trim() : getRoleLabel(value);
    seen.add(value);
    out.push({ value, label });
  }
  return out;
}

export type RoleListSource = "place" | "type" | "default";

/**
 * Which roles an activity offers. A place's own list wins, then the type's
 * saved list, then the built-in one for the type. `featured` is what shows up
 * front (null: show everything); `all` adds the general roles behind "More…",
 * and every saved custom role, so a chip can always be labelled.
 */
export function resolveActivityRoles(
  type: string | null | undefined,
  placeRoles: RoleTypeOption[] | null,
  typeRoles: RoleTypeOption[] | null,
): { featured: string[] | null; all: RoleTypeOption[]; source: RoleListSource } {
  const saved = placeRoles ?? typeRoles;
  const source: RoleListSource = placeRoles ? "place" : typeRoles ? "type" : "default";
  const builtIn = activityRoleTypesFor(type);
  if (!saved) return { featured: featuredActivityRolesFor(type), all: builtIn, source };
  const seen = new Set(saved.map((t) => t.value));
  return {
    featured: saved.map((t) => t.value),
    all: [...saved, ...builtIn.filter((t) => !seen.has(t.value))],
    source,
  };
}

/** Get the display label for a role, handling unknown roles gracefully */
export function getRoleLabel(
  role: string,
  types: RoleTypeOption[] = ACTIVITY_ROLE_TYPES,
): string {
  if (role === "other") return "Other";
  const match =
    types.find((t) => t.value === role) ??
    Object.values(ACTIVITY_ROLES_BY_TYPE).flatMap((ts) => ts ?? []).find((t) => t.value === role);
  return match?.label ?? role;
}
